import { PaginationMeta } from '../types/pagination';
import {
  PromptEvalListParams,
  PromptEvalPaginatedRaw,
  PromptEvalQuestion,
  PromptEvalQuestionCreateRequest,
  PromptEvalQuestionReorderDirection,
  PromptEvalQuestionReorderRequest,
  PromptEvalQuestionSet,
  PromptEvalQuestionSetCreateRequest,
  PromptEvalQuestionSetUpdateRequest,
  PromptEvalQuestionUpdateRequest,
  PromptEvalScenario,
  PromptEvalScenarioCreateRequest,
  PromptEvalScenarioUpdateRequest,
  PromptEvalSuite,
  PromptEvalSuiteClientAddRequest,
  PromptEvalSuiteClientMember,
  PromptEvalSuiteCreateRequest,
  PromptEvalSuiteScenarioAddRequest,
  PromptEvalSuiteScenarioMember,
  PromptEvalSuiteUpdateRequest,
  PromptEvalOpenAIConfig,
  PromptEvalOpenAIConfigUpdateRequest,
  PromptEvalRun,
  PromptEvalRunBulkDeleteResponse,
  PromptEvalRunCreateRequest,
  PromptEvalRunDeletePreviewResponse,
  PromptEvalApplySuggestionRequest,
  PromptEvalApplySuggestionResponse,
  PromptEvalRunItem,
  PromptEvalRunItemReviewRequest,
  PromptEvalRunListParams,
  PromptEvalRunReplayRequest
} from '../types/promptEval';
import { ApiClient, ApiError } from './apiClient';

export interface PromptEvalPaginatedResult<T> {
  data: T[];
  meta: PaginationMeta;
}

const emptyMeta = (page = 1, pageSize = 10): PaginationMeta => ({
  page,
  limit: pageSize,
  total: 0,
  totalPages: 0
});

const normalizePaginated = <T>(
  raw: PromptEvalPaginatedRaw<T> | null | undefined,
  fallbackPage: number,
  fallbackPageSize: number
): PromptEvalPaginatedResult<T> => {
  const items = raw?.items ?? raw?.data ?? [];
  const page = Number(raw?.meta?.page ?? raw?.page ?? fallbackPage);
  const limit = Number(
    raw?.meta?.limit ?? raw?.meta?.pageSize ?? raw?.pageSize ?? raw?.limit ?? fallbackPageSize
  );
  const total = Number(raw?.meta?.total ?? raw?.total ?? items.length);
  const totalPages = Number(
    raw?.meta?.totalPages ?? (limit > 0 ? Math.ceil(total / limit) : 0)
  );

  return {
    data: items,
    meta: { page, limit, total, totalPages }
  };
};

export class PromptEvalService {
  constructor(private api: ApiClient) {}

  getOpenAIConfig() {
    return this.api
      .get<PromptEvalOpenAIConfig>('/api/prompt-eval/openai-config')
      .then(result => {
        if (!result.success || !result.data) {
          throw new ApiError(0, result.error ?? 'Failed to load Prompt Eval OpenAI config');
        }
        return result.data;
      });
  }

  updateOpenAIConfig(payload: PromptEvalOpenAIConfigUpdateRequest) {
    return this.api
      .put<PromptEvalOpenAIConfig>('/api/prompt-eval/openai-config', payload)
      .then(result => {
        if (!result.success || !result.data) {
          throw new ApiError(0, result.error ?? 'Failed to save Prompt Eval OpenAI key');
        }
        return result.data;
      });
  }

  deleteOpenAIConfig() {
    return this.api
      .delete<PromptEvalOpenAIConfig>('/api/prompt-eval/openai-config')
      .then(result => {
        if (!result.success || !result.data) {
          throw new ApiError(0, result.error ?? 'Failed to remove Prompt Eval OpenAI key');
        }
        return result.data;
      });
  }

  listQuestionSets(params?: PromptEvalListParams) {
    const page = params?.page ?? 1;
    const pageSize = params?.pageSize ?? 10;
    const search = params?.search?.trim();
    const query: Record<string, string | number | undefined> = {
      page,
      pageSize
    };
    if (params?.includeInactive) {
      query.includeInactive = 'true';
    }
    if (search) {
      query.search = search;
    }

    return this.api
      .get<PromptEvalPaginatedRaw<PromptEvalQuestionSet>>('/api/prompt-eval/question-sets', query)
      .then(result =>
        normalizePaginated(
          result.success ? result.data : null,
          page,
          pageSize
        )
      );
  }

  getQuestionSet(id: number) {
    return this.api
      .get<PromptEvalQuestionSet>(`/api/prompt-eval/question-sets/${id}`)
      .then(result => (result.success && result.data ? result.data : null));
  }

  createQuestionSet(payload: PromptEvalQuestionSetCreateRequest) {
    return this.api
      .post<PromptEvalQuestionSet>('/api/prompt-eval/question-sets', payload)
      .then(result => {
        if (!result.success || !result.data) {
          throw new ApiError(0, result.error ?? 'Failed to create question set');
        }
        return result.data;
      });
  }

  updateQuestionSet(id: number, payload: PromptEvalQuestionSetUpdateRequest) {
    return this.api
      .put<PromptEvalQuestionSet>(`/api/prompt-eval/question-sets/${id}`, payload)
      .then(result => {
        if (!result.success || !result.data) {
          throw new ApiError(0, result.error ?? 'Failed to update question set');
        }
        return result.data;
      });
  }

  deleteQuestionSet(id: number) {
    return this.api.delete<void>(`/api/prompt-eval/question-sets/${id}`).then(result => {
      if (!result.success) {
        throw new ApiError(0, result.error ?? 'Failed to delete question set');
      }
    });
  }

  listQuestions(setId: number, params?: PromptEvalListParams) {
    const page = params?.page ?? 1;
    const pageSize = params?.pageSize ?? 10;
    const search = params?.search?.trim();
    const query: Record<string, string | number | undefined> = {
      page,
      pageSize
    };
    if (search) {
      query.search = search;
    }

    return this.api
      .get<PromptEvalQuestion[] | PromptEvalPaginatedRaw<PromptEvalQuestion>>(
        `/api/prompt-eval/question-sets/${setId}/questions`,
        query
      )
      .then(result => {
        if (!result.success || !result.data) {
          return { data: [] as PromptEvalQuestion[], meta: emptyMeta(page, pageSize) };
        }
        const raw = result.data;
        if (Array.isArray(raw)) {
          const total = raw.length;
          const start = Math.max(0, (page - 1) * pageSize);
          const slice = raw.slice(start, start + pageSize);
          return {
            data: slice,
            meta: {
              page,
              limit: pageSize,
              total,
              totalPages: pageSize > 0 ? Math.ceil(total / pageSize) : 0
            }
          };
        }
        return normalizePaginated(raw, page, pageSize);
      });
  }

  createQuestion(setId: number, payload: PromptEvalQuestionCreateRequest) {
    return this.api
      .post<PromptEvalQuestion>(`/api/prompt-eval/question-sets/${setId}/questions`, payload)
      .then(result => {
        if (!result.success || !result.data) {
          throw new ApiError(0, result.error ?? 'Failed to create question');
        }
        return result.data;
      });
  }

  updateQuestion(id: number, payload: PromptEvalQuestionUpdateRequest) {
    return this.api
      .put<PromptEvalQuestion>(`/api/prompt-eval/questions/${id}`, payload)
      .then(result => {
        if (!result.success || !result.data) {
          throw new ApiError(0, result.error ?? 'Failed to update question');
        }
        return result.data;
      });
  }

  reorderQuestion(id: number, direction: PromptEvalQuestionReorderDirection) {
    const payload: PromptEvalQuestionReorderRequest = { direction };
    return this.api
      .post<PromptEvalQuestion>(`/api/prompt-eval/questions/${id}/reorder`, payload)
      .then(result => {
        if (!result.success || !result.data) {
          throw new ApiError(0, result.error ?? 'Failed to reorder question');
        }
        return result.data;
      });
  }

  deleteQuestion(id: number) {
    return this.api.delete<void>(`/api/prompt-eval/questions/${id}`).then(result => {
      if (!result.success) {
        throw new ApiError(0, result.error ?? 'Failed to delete question');
      }
    });
  }

  listScenarios(params?: PromptEvalListParams) {
    const page = params?.page ?? 1;
    const pageSize = params?.pageSize ?? 10;
    const query: Record<string, string | number | undefined> = {
      page,
      pageSize
    };
    if (params?.includeInactive) {
      query.includeInactive = 'true';
    }
    const search = params?.search?.trim();
    if (search) {
      query.search = search;
    }

    return this.api
      .get<PromptEvalPaginatedRaw<PromptEvalScenario>>('/api/prompt-eval/scenarios', query)
      .then(result =>
        normalizePaginated(
          result.success ? result.data : null,
          page,
          pageSize
        )
      );
  }

  getScenario(id: number) {
    return this.api
      .get<PromptEvalScenario>(`/api/prompt-eval/scenarios/${id}`)
      .then(result => (result.success && result.data ? result.data : null));
  }

  createScenario(payload: PromptEvalScenarioCreateRequest) {
    return this.api
      .post<PromptEvalScenario>('/api/prompt-eval/scenarios', payload)
      .then(result => {
        if (!result.success || !result.data) {
          throw new ApiError(0, result.error ?? 'Failed to create scenario');
        }
        return result.data;
      });
  }

  updateScenario(id: number, payload: PromptEvalScenarioUpdateRequest) {
    return this.api
      .put<PromptEvalScenario>(`/api/prompt-eval/scenarios/${id}`, payload)
      .then(result => {
        if (!result.success || !result.data) {
          throw new ApiError(0, result.error ?? 'Failed to update scenario');
        }
        return result.data;
      });
  }

  deleteScenario(id: number) {
    return this.api.delete<void>(`/api/prompt-eval/scenarios/${id}`).then(result => {
      if (!result.success) {
        throw new ApiError(0, result.error ?? 'Failed to delete scenario');
      }
    });
  }

  listSuites(params?: PromptEvalListParams) {
    const page = params?.page ?? 1;
    const pageSize = params?.pageSize ?? 10;
    const query: Record<string, string | number | undefined> = {
      page,
      pageSize
    };
    if (params?.includeInactive) {
      query.includeInactive = 'true';
    }
    const search = params?.search?.trim();
    if (search) {
      query.search = search;
    }

    return this.api
      .get<PromptEvalPaginatedRaw<PromptEvalSuite>>('/api/prompt-eval/suites', query)
      .then(result =>
        normalizePaginated(result.success ? result.data : null, page, pageSize)
      );
  }

  getSuite(id: number) {
    return this.api
      .get<PromptEvalSuite>(`/api/prompt-eval/suites/${id}`)
      .then(result => (result.success && result.data ? result.data : null));
  }

  createSuite(payload: PromptEvalSuiteCreateRequest) {
    return this.api.post<PromptEvalSuite>('/api/prompt-eval/suites', payload).then(result => {
      if (!result.success || !result.data) {
        throw new ApiError(0, result.error ?? 'Failed to create suite');
      }
      return result.data;
    });
  }

  updateSuite(id: number, payload: PromptEvalSuiteUpdateRequest) {
    return this.api.put<PromptEvalSuite>(`/api/prompt-eval/suites/${id}`, payload).then(result => {
      if (!result.success || !result.data) {
        throw new ApiError(0, result.error ?? 'Failed to update suite');
      }
      return result.data;
    });
  }

  deleteSuite(id: number) {
    return this.api.delete<void>(`/api/prompt-eval/suites/${id}`).then(result => {
      if (!result.success) {
        throw new ApiError(0, result.error ?? 'Failed to delete suite');
      }
    });
  }

  addSuiteScenario(suiteId: number, payload: PromptEvalSuiteScenarioAddRequest) {
    return this.api
      .post<PromptEvalSuiteScenarioMember>(`/api/prompt-eval/suites/${suiteId}/scenarios`, payload)
      .then(result => {
        if (!result.success || !result.data) {
          throw new ApiError(0, result.error ?? 'Failed to add scenario to suite');
        }
        return result.data;
      });
  }

  removeSuiteScenario(suiteId: number, memberId: number) {
    return this.api
      .delete<void>(`/api/prompt-eval/suites/${suiteId}/scenarios/${memberId}`)
      .then(result => {
        if (!result.success) {
          throw new ApiError(0, result.error ?? 'Failed to remove scenario from suite');
        }
      });
  }

  addSuiteClient(suiteId: number, payload: PromptEvalSuiteClientAddRequest) {
    return this.api
      .post<PromptEvalSuiteClientMember>(`/api/prompt-eval/suites/${suiteId}/clients`, payload)
      .then(result => {
        if (!result.success || !result.data) {
          throw new ApiError(0, result.error ?? 'Failed to add client to suite');
        }
        return result.data;
      });
  }

  removeSuiteClient(suiteId: number, memberId: number) {
    return this.api
      .delete<void>(`/api/prompt-eval/suites/${suiteId}/clients/${memberId}`)
      .then(result => {
        if (!result.success) {
          throw new ApiError(0, result.error ?? 'Failed to remove client from suite');
        }
      });
  }

  listRuns(params?: PromptEvalRunListParams) {
    const page = params?.page ?? 1;
    const pageSize = params?.pageSize ?? 10;
    const query: Record<string, string | number | undefined> = {
      page,
      pageSize
    };
    if (params?.scenarioId != null) {
      query.scenarioId = params.scenarioId;
    }
    if (params?.suiteId != null) {
      query.suiteId = params.suiteId;
    }
    if (params?.chatUserId) {
      query.chatUserId = params.chatUserId;
    }
    if (params?.status) {
      query.status = params.status;
    }

    return this.api
      .get<PromptEvalPaginatedRaw<PromptEvalRun>>('/api/prompt-eval/runs', query)
      .then(result =>
        normalizePaginated(result.success ? result.data : null, page, pageSize)
      );
  }

  getRun(id: number) {
    return this.api
      .get<PromptEvalRun>(`/api/prompt-eval/runs/${id}`)
      .then(result => (result.success && result.data ? result.data : null));
  }

  updateRunItemReview(
    runId: number,
    itemId: number,
    payload: PromptEvalRunItemReviewRequest
  ) {
    return this.api
      .patch<PromptEvalRunItem>(`/api/prompt-eval/runs/${runId}/items/${itemId}`, payload)
      .then(result => {
        if (!result.success || !result.data) {
          throw new ApiError(0, result.error ?? 'Failed to update item review');
        }
        return result.data;
      });
  }

  clearRunItemReview(runId: number, itemId: number) {
    return this.api
      .delete<PromptEvalRunItem | void>(`/api/prompt-eval/runs/${runId}/items/${itemId}/review`)
      .then(result => {
        if (!result.success) {
          throw new ApiError(0, result.error ?? 'Failed to clear item review');
        }
        return result.data ?? null;
      });
  }

  coachRunItem(runId: number, itemId: number) {
    return this.api
      .post<PromptEvalRunItem>(`/api/prompt-eval/runs/${runId}/items/${itemId}/coach`)
      .then(result => {
        if (!result.success || !result.data) {
          throw new ApiError(0, result.error ?? 'Failed to generate coach suggestions');
        }
        return result.data;
      });
  }

  applySuggestion(
    runId: number,
    itemId: number,
    payload: PromptEvalApplySuggestionRequest
  ) {
    return this.api
      .post<PromptEvalApplySuggestionResponse>(
        `/api/prompt-eval/runs/${runId}/items/${itemId}/apply-suggestion`,
        payload
      )
      .then(result => {
        if (!result.success || !result.data) {
          throw new ApiError(0, result.error ?? 'Failed to apply suggestion');
        }
        return result.data;
      });
  }

  /** Resolve a run item (includes `runId`) for deep links from prompt version stamps. */
  getRunItem(itemId: number) {
    return this.api
      .get<PromptEvalRunItem>(`/api/prompt-eval/run-items/${itemId}`)
      .then(result => (result.success && result.data ? result.data : null));
  }

  createRun(payload: PromptEvalRunCreateRequest) {
    return this.api.post<PromptEvalRun>('/api/prompt-eval/runs', payload).then(result => {
      if (!result.success || !result.data) {
        throw new ApiError(0, result.error ?? 'Failed to create run');
      }
      return result.data;
    });
  }

  replayRun(id: number, payload?: PromptEvalRunReplayRequest) {
    return this.api
      .post<PromptEvalRun>(`/api/prompt-eval/runs/${id}/replay`, payload ?? {})
      .then(result => {
        if (!result.success || !result.data) {
          throw new ApiError(0, result.error ?? 'Failed to replay run');
        }
        return result.data;
      });
  }

  previewDeleteRuns(ids: number[]) {
    return this.api
      .post<PromptEvalRunDeletePreviewResponse>('/api/prompt-eval/runs/delete-preview', { ids })
      .then(result => {
        if (!result.success || !result.data) {
          throw new ApiError(0, result.error ?? 'Failed to preview run delete');
        }
        return result.data;
      });
  }

  bulkDeleteRuns(ids: number[]) {
    return this.api
      .post<PromptEvalRunBulkDeleteResponse>('/api/prompt-eval/runs/bulk-delete', { ids })
      .then(result => {
        if (!result.success || !result.data) {
          throw new ApiError(0, result.error ?? 'Failed to delete runs');
        }
        return result.data;
      });
  }

  /** Single-run delete (no replay cascade). BO UI should use preview + bulk-delete. */
  deleteRun(id: number) {
    return this.api.delete<void>(`/api/prompt-eval/runs/${id}`).then(result => {
      if (!result.success) {
        throw new ApiError(0, result.error ?? 'Failed to delete run');
      }
    });
  }
}
